import { useTranslation } from 'react-i18next'
import { AlertCircle, RotateCcw, RefreshCw, Loader2 } from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import type { AuditJob } from '@/lib/api/amazon-listing'
import { useRestartAudit } from '@/hooks/useAmazonAudit'

interface AuditFailedScreenProps {
  job: AuditJob
}

export function AuditFailedScreen({ job }: AuditFailedScreenProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { productId } = useParams<{ productId: string }>()
  const restart = useRestartAudit()

  const handleRestart = () => {
    restart.mutate(job.id, {
      onSuccess: (result) => {
        const target = productId ?? result.audit_product_id
        if (target) {
          navigate(`/v2/tools/product-listing-audit/${target}?audit_id=${result.job_id}`, { replace: true })
        }
      },
    })
  }

  const restartLabel = t('amazonListing.failed.restartButton', 'Audit neu starten')
  const restartError = restart.error?.message

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-8">
      <div className="w-full max-w-md space-y-6 text-center">
        <div className="flex flex-col items-center gap-3">
          <div className="p-4 rounded-full bg-red-100 dark:bg-red-900/30">
            <AlertCircle className="h-10 w-10 text-red-500" />
          </div>
          <h2 className="text-xl font-bold text-neutral-900 dark:text-white">
            {t('amazonListing.failed.title')}
          </h2>
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            {t('amazonListing.failed.description')}
          </p>
        </div>

        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-left">
          <p className="text-xs font-mono text-red-600 dark:text-red-400 break-all">
            {t('amazonListing.failed.jobId')}: {job.id}
          </p>
          {job.asin && (
            <p className="text-xs font-mono text-red-500 dark:text-red-500 mt-1">
              ASIN: {job.asin}
            </p>
          )}
          {job.error_message && (
            <p className="text-xs text-red-500 dark:text-red-400 mt-2 whitespace-pre-wrap">
              {job.error_message}
            </p>
          )}
        </div>

        {restartError && (
          <p className="text-xs text-red-500 dark:text-red-400">{restartError}</p>
        )}

        <div className="flex flex-wrap items-center justify-center gap-3">
          <button
            onClick={handleRestart}
            disabled={restart.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-sm font-medium text-white transition-colors"
          >
            {restart.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {restartLabel}
          </button>
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 text-sm font-medium text-neutral-700 dark:text-neutral-300 transition-colors"
          >
            <RotateCcw className="h-4 w-4" />
            {t('amazonListing.failed.backButton')}
          </button>
        </div>
      </div>
    </div>
  )
}
